/* eslint-disable camelcase */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { Dropdown } from 'react-bootstrap';
import classNames from 'classnames';
import ReactTooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { user } from '../../../../redux/user';
import { showNotification } from '../../../../redux/notification';
import utils from '../../../../utils/utils';
import WithOnlyOnDesktop from '../../../../components/onlyOnDesktop/withOnlyOnDesktop';
import { useFlexLocationsQuery, useUpdateLocationsStatusMutation } from '../../../../redux/flex-professional-locations';
import { useLazyGetUpcomingShiftsByProfessionalQuery } from '../../../../redux/managementCompanyWorkforce';
import Permissions from '../../../../constants/Permissions';
import IsAuthorizedForManagementCompany from '../../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import { ReactComponent as KebobIcon } from '../../../../assets/images/more-horizontal.svg';
import { ReactComponent as BulkActionIcon } from '../../../../assets/images/bounding-box.svg';
import { ReactComponent as BulkActionWhiteIcon } from '../../../../assets/images/bounding-box-white.svg';
import infoIcon from '../../../../assets/images/info-icon-black.svg';
import LocationsStatusUpdateConfirmation from './LocationsStatusUpdateConfirmation';

import './FlexProfessionalLocations.scss';

const FlexProfessionalLocations = ({ professionalName }) => {
	const timer = useRef(null);

	const { caregiverId, managementCompanyId } = useParams();
	const dispatch = useDispatch();
	const userData = useSelector(user, shallowEqual);

	const [updateAnimation, setUpdateAnimation] = useState(false);
	const [bulkUpdateActive, setBulkUpdateActive] = useState(false);
	const [selectedLocations, setSelectedLocations] = useState([]);
	const [selectedAction, setSelectedAction] = useState(null);
	const [upcomingShiftsCount, setUpcomingShiftsCount] = useState(0);

	const [getUpcomingShifts, { isFetching: loadingUpcomingShifts }] = useLazyGetUpcomingShiftsByProfessionalQuery();

	const { data: locations, isLoading, error, isError } = useFlexLocationsQuery(
		{ managementCompanyId, caregiverId },
		{
			refetchOnMountOrArgChange: true
		}
	);
	const [updateLocationsStatus, { isLoading: loadingUpdate }] = useUpdateLocationsStatusMutation();

	useEffect(() => {
		return () => clearTimeout(timer.current);
	}, []);

	const notAccessible = useMemo(() => isError && error?.status === 403, [isError, error]);

	const statusTemplate = (data) => (data.is_enabled ? 'Enabled' : 'Disabled');

	const onSingleUpdate = async (facility, action) => {
		setSelectedAction(action);
		setSelectedLocations([facility]);
		if (action === 'DISABLE') {
			try {
				const queryParams = `facilityIds=${facility.facility_id}`;
				const upcomingShiftsData = await getUpcomingShifts({
					managementCompanyId,
					caregiverId,
					queryParams
				}).unwrap();
				setUpcomingShiftsCount(upcomingShiftsData ? upcomingShiftsData.length : 0);
			} catch (e) {
				dispatch(showNotification('error', `There was an issue completing this action. Please try again.`));
				setSelectedAction(null);
			}
		}
	};

	const cancelBulkUpdate = () => {
		setBulkUpdateActive(false);
		setSelectedLocations([]);
	};

	const handleStatusChangeAction = async () => {
		try {
			await updateLocationsStatus({
				managementCompanyId,
				caregiverId,
				body: {
					facilityIds: selectedLocations.map((item) => item.facility_id),
					status: selectedAction === 'DISABLE' ? 'DISABLED' : 'ENABLED'
				}
			}).unwrap();
			const statusText = `${selectedAction.toLowerCase()}d`;
			const locationText =
				selectedLocations.length === 1 ? selectedLocations[0].facility_name : `${selectedLocations.length} locations`;
			const message = `${professionalName} has been ${statusText} at ${locationText}.`;
			dispatch(showNotification('success', message));

			setUpdateAnimation(true);
			setBulkUpdateActive(false);
			setSelectedAction(null);

			timer.current = setTimeout(() => {
				setUpdateAnimation(false);
				setSelectedLocations([]);
			}, 2700);
		} catch (err) {
			setSelectedAction(null);
			setUpdateAnimation(false);
			const fallbackMessage = `There was an issue completing this action. Please try again.`;
			dispatch(showNotification('error', utils.parseRTKQueryValidationError(err, fallbackMessage)));
		}
	};

	const onBulkDisable = async () => {
		setSelectedAction('DISABLE');
		try {
			const queryParams = `facilityIds=${selectedLocations.map((item) => item.facility_id).join(',')}`;
			const upcomingShiftsData = await getUpcomingShifts({
				managementCompanyId,
				caregiverId,
				queryParams
			}).unwrap();
			setUpcomingShiftsCount(upcomingShiftsData ? upcomingShiftsData.length : 0);
		} catch (e) {
			dispatch(showNotification('error', `There was an issue completing this action. Please try again.`));
			setSelectedAction(null);
		}
	};

	const onCancelConfirmation = () => {
		setSelectedAction(null);
		setUpcomingShiftsCount(0);
		if (!bulkUpdateActive) {
			setSelectedLocations([]);
		}
	};

	const hasEditPermissionForFacility = (facilityId) =>
		userData.permissionsByFacility?.[facilityId]?.includes('PORTAL.FLEX.ENABLE_DISABLE_STAFF');

	const hasEditPermissionForManagementCompany = utils.isAuthorizedExact(
		Permissions.WORKFORCE_LOCATIONS_EDIT,
		userData.managementCompanyPermissions[managementCompanyId]
	);

	const rowClass = (data) => ({
		'disabled-status-row': !data.is_enabled,
		'disabled-edit-row': bulkUpdateActive && !hasEditPermissionForFacility(data.facility_id),
		'highlight-updated-row': updateAnimation && selectedLocations.find((item) => item.facility_id === data.facility_id)
	});

	const isRowSelectable = (event) => bulkUpdateActive && hasEditPermissionForFacility(event.data.facility_id);

	const actionsTemplate = (item) => {
		if (!hasEditPermissionForFacility(item.facility_id)) return '';
		return (
			<div className="action-menu-container">
				<Dropdown>
					<Dropdown.Toggle
						className={classNames('action-menu', { disabled: bulkUpdateActive || loadingUpdate || updateAnimation })}
					>
						<KebobIcon alt="Show More" className="action-menu-icon" />
					</Dropdown.Toggle>
					<Dropdown.Menu>
						<Dropdown.Item
							className="action-menu-item"
							onClick={() => onSingleUpdate(item, item.is_enabled ? 'DISABLE' : 'ENABLE')}
						>
							{item.is_enabled ? 'Disable' : 'Enable'}
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown>
			</div>
		);
	};

	const enabledLocationsCount = locations?.filter((loc) => loc.is_enabled).length;

	const showConfirmationModal = !!selectedAction;

	const handleSelectionChange = (event) => {
		if (!bulkUpdateActive) return;

		const newSelection = event.value.filter((item) => hasEditPermissionForFacility(item.facility_id));
		setSelectedLocations(newSelection);
	};

	return (
		<WithOnlyOnDesktop>
			<div className="flex-professionals-locations">
				<div className="toolbar">
					<div className="toolbar-item">
						<span>
							This professional is enabled at{' '}
							<b>
								{enabledLocationsCount} of {locations?.length} locations
							</b>
							.
						</span>
						<OverlayTrigger
							className="tooltip-component"
							placement="bottom-start"
							delay={{ show: 100, hide: 400 }}
							overlay={
								<ReactTooltip
									className="locations-info-tooltip"
									id="info-tooltip"
									scrollHide={false}
									resizeHide={false}
								>
									<div className="tooltip-content">
										All locations are displayed below. The locations where you have modify permission can be updated
										using the bulk actions or individual actions menu.
									</div>
								</ReactTooltip>
							}
						>
							<img src={infoIcon} alt="Ascending" className="info-icon" />
						</OverlayTrigger>
					</div>
					<IsAuthorizedForManagementCompany
						exactPermission={Permissions.WORKFORCE_LOCATIONS_EDIT}
						managementCompanyId={managementCompanyId}
					>
						{!bulkUpdateActive ? (
							<Button
								disabled={notAccessible || loadingUpdate || updateAnimation}
								className="btn actions-button"
								onClick={() => setBulkUpdateActive(true)}
							>
								{notAccessible || loadingUpdate || updateAnimation ? (
									<BulkActionWhiteIcon alt="Bulk actions" />
								) : (
									<BulkActionIcon alt="Bulk actions" />
								)}
								<div className="button-label">Bulk actions</div>
							</Button>
						) : (
							<div className="d-flex">
								<button type="button" onClick={cancelBulkUpdate} className="btn-cancel">
									Cancel
								</button>
								<Dropdown>
									<Dropdown.Toggle
										variant="primary"
										className={classNames('bulk-update-menu', { disabled: !selectedLocations?.length })}
									>
										Bulk actions
									</Dropdown.Toggle>
									<Dropdown.Menu>
										<Dropdown.Item className="action-menu-item" onClick={() => setSelectedAction('ENABLE')}>
											Enable
										</Dropdown.Item>
										<Dropdown.Item className="action-menu-item" onClick={onBulkDisable}>
											Disable
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>
							</div>
						)}
					</IsAuthorizedForManagementCompany>
				</div>

				<DataTable
					value={locations}
					scrollHeight="flex"
					sortMode="multiple"
					tableLayout="fixed"
					size="small"
					stripedRows
					rowClassName={rowClass}
					loading={isLoading}
					removableSort
					dataKey="facility_id"
					selection={selectedLocations}
					onSelectionChange={handleSelectionChange}
					selectionMode="multiple"
					isDataSelectable={isRowSelectable}
				>
					{bulkUpdateActive && <Column selectionMode="multiple" style={{ width: '3%' }} />}
					<Column field="facility_name" header="Facility" style={{ width: '30%' }} sortable />
					<Column field="state" header="State" style={{ width: '8%' }} sortable />
					<Column field="city_name" header="City" style={{ width: '15%' }} sortable />
					<Column field="zip_code" header="ZIP" style={{ width: '8%' }} sortable />
					<Column header="Status" style={{ width: '10%' }} sortable field={statusTemplate} className="status" />
					{hasEditPermissionForManagementCompany && (
						<Column
							field="actions"
							header="Actions"
							style={{ maxWidth: '80px', width: '3%' }}
							headerClassName={classNames({ 'disabled-column-header': bulkUpdateActive })}
							body={actionsTemplate}
						/>
					)}
				</DataTable>
			</div>

			{showConfirmationModal && (
				<LocationsStatusUpdateConfirmation
					loading={loadingUpdate || loadingUpcomingShifts}
					onCancel={onCancelConfirmation}
					handleStatusChangeAction={handleStatusChangeAction}
					selectedAction={selectedAction}
					locations={selectedLocations}
					upcomingShiftsCount={upcomingShiftsCount}
				/>
			)}
		</WithOnlyOnDesktop>
	);
};

export default FlexProfessionalLocations;
